import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Table, Button, Space, Input, Modal, Form, message, Typography,
    Popconfirm, Tooltip, Dropdown
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    TeamOutlined,
    MoreOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { groupService } from '../services/api';

const { Title, Text } = Typography;

const GroupsDrawer = ({ visible, onClose, onSelectGroup }) => {
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [modal, setModal] = useState({ visible: false, mode: 'create', data: null });
    const [form] = Form.useForm();

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const response = await groupService.list(searchText);
            if (response.status === 'success') {
                setGroups(response.data);
            } else {
                message.error('Error al cargar grupos');
            }
        } catch (error) {
            console.error('Fetch groups error:', error);
            message.error('Error al conectar con el servidor');
        } finally {
            setLoading(false);
        }
    }, [searchText]);

    useEffect(() => {
        if (visible) {
            fetchGroups();
        }
    }, [visible, fetchGroups]);

    const handleOpenModal = (mode, record = null) => {
        form.resetFields();
        if (mode === 'edit' && record) {
            form.setFieldsValue(record);
        }
        setModal({ visible: true, mode, data: record });
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            let res;
            if (modal.mode === 'create') {
                res = await groupService.create(values);
            } else {
                res = await groupService.update(modal.data.id, values);
            }

            if (res.status === 'success') {
                message.success(`Grupo ${modal.mode === 'create' ? 'creado' : 'actualizado'} correctamente`);
                setModal({ ...modal, visible: false });
                fetchGroups();
            } else {
                message.error(res.message || 'Error en la operación');
            }
        } catch (error) {
            if (error.errorFields) return;
            message.error('Error al guardar el grupo: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await groupService.delete(id);
            if (res.status === 'success') {
                message.success('Grupo eliminado correctamente');
                fetchGroups();
            } else {
                message.error(res.message || 'No se pudo eliminar el grupo');
            }
        } catch (error) {
            message.error('Error al eliminar: ' + (error.response?.data?.message || error.message));
        }
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Descripción',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => text || <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin descripción</Text>
        },
        {
            title: 'Participantes',
            dataIndex: 'member_count',
            key: 'members',
            align: 'center',
            render: (count) => (
                <Space>
                    <TeamOutlined style={{ color: '#8c8c8c' }} />
                    <Text>{count}</Text>
                </Space>
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const items = [
                    {
                        key: 'view',
                        label: 'Ver Participantes',
                        icon: <EyeOutlined />,
                        onClick: () => {
                            onSelectGroup(record.id);
                            onClose();
                        }
                    },
                    {
                        key: 'edit',
                        label: 'Editar',
                        icon: <EditOutlined />,
                        onClick: () => handleOpenModal('edit', record)
                    },
                    {
                        key: 'delete',
                        label: 'Eliminar',
                        icon: <DeleteOutlined />,
                        danger: true,
                    }
                ];

                return (
                    <Dropdown
                        menu={{
                            items,
                            onClick: ({ key }) => {
                                if (key === 'delete') {
                                    Modal.confirm({
                                        title: '¿Eliminar Grupo?',
                                        content: `¿Estás seguro que deseas eliminar el grupo "${record.name}"? Esta acción no se puede deshacer.`,
                                        okText: 'Sí, Eliminar',
                                        okType: 'danger',
                                        cancelText: 'Cancelar',
                                        onOk: () => handleDelete(record.id)
                                    });
                                }
                            }
                        }}
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '95%' }}>
                        <span>Gestión de Grupos</span>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal('create')}
                        >
                            Nuevo Grupo
                        </Button>
                    </div>
                }
                placement="right"
                onClose={onClose}
                open={visible}
                width={700}
                bodyStyle={{ padding: '20px' }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Buscar grupos..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                <Table
                    columns={columns}
                    dataSource={groups}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    size="middle"
                />
            </Drawer>

            <Modal
                title={modal.mode === 'create' ? "Crear Nuevo Grupo" : "Editar Grupo"}
                open={modal.visible}
                onOk={handleModalSubmit}
                onCancel={() => setModal({ ...modal, visible: false })}
                okText="Guardar"
                cancelText="Cancelar"
                centered
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ name: '', description: '' }}
                >
                    <Form.Item
                        label="Nombre del Grupo"
                        name="name"
                        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                    >
                        <Input placeholder="Ej. Donantes Recurrentes" />
                    </Form.Item>
                    <Form.Item
                        label="Descripción"
                        name="description"
                    >
                        <Input.TextArea rows={4} placeholder="Escribe una breve descripción del propósito de este grupo..." />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default GroupsDrawer;
